import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Grid,
    TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import bgLogin from "../../../../assets/images/bg-login.svg";
import logo from "../../../../assets/images/logo-login-new.svg";
import Loading from "../../../components/loading/loading";
import { Api } from "../../../services/api-config";
import { URLS } from "../../../services/app-urls";
import { fetchHubStatus } from "../../../services/hub-settings";
import { translate } from "../../../translate/translate";
import { publishAuthMessage } from "../../../utils/auth-broadcast";
import { authTokenStorage, isTokenExpired } from "../../../utils/auth-token-storage";
import { consumePendingRedirect } from "../../../utils/post-login-redirect";
import { redirectToLoginHub } from "../../../utils/redirect-login-utils";
import "./login.component.css";

const LoginComponent = () => {
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [fallbackAvailable, setFallbackAvailable] = useState(false);

    const [username, setUsername] = useState<string>("");
    const [usernameMessage, setUsernameMessage] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordMessage, setPasswordMessage] = useState<string>("");
    const [submitting, setSubmitting] = useState(false);
    const [generalError, setGeneralError] = useState<string | null>(null);

    const [passwordTranslate, setPasswordTranslate] = useState<string>(
        translate.t("password")
    );
    const [usernameLabel, setUsernameLabel] = useState<string>(
        translate.t("username")
    );

    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        dayjs.locale(translate.language === "pt" ? "pt-BR" : translate.language);

        fetchHubStatus()
            .then((status) => {
                if (cancelled) return;
                const isAvailable = Boolean(
                    status?.local_fallback_available || status?.hub_online === false
                );

                if (!isAvailable) {
                    // Hub is online and fallback is not available/forced.
                    // If the user already has a valid session, go home; otherwise redirect to Hub.
                    const existingToken = authTokenStorage.get();
                    if (existingToken && !isTokenExpired(existingToken)) {
                        navigate("/home");
                    } else {
                        redirectToLoginHub();
                    }
                } else {
                    setFallbackAvailable(true);
                    setCheckingStatus(false);
                }
            })
            .catch(() => {
                if (cancelled) return;
                // If checking fails (e.g. Hub completely down), allow fallback form
                setFallbackAvailable(true);
                setCheckingStatus(false);
            });

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleLanguageChange = (lng: string) => {
        translate.changeLanguage(lng).then(() => {
            dayjs.locale(lng === "pt" ? "pt-BR" : lng);
            validate();
        });
    };

    const validate = () => {
        let usernameOk = false;
        let passwordOk = false;

        if (username.length === 0) {
            usernameOk = false;
            setUsernameMessage(translate.t("required_field"));
        } else if (username.length > 64) {
            usernameOk = false;
            setUsernameMessage(translate.t("exceeds_character_limit"));
        } else {
            usernameOk = true;
            setUsernameMessage("");
        }

        if (password.length === 0) {
            passwordOk = false;
            setPasswordMessage(translate.t("required_field"));
        } else if (password.length > 64) {
            passwordOk = false;
            setPasswordMessage(translate.t("exceeds_character_limit"));
        } else {
            passwordOk = true;
            setPasswordMessage("");
        }

        setUsernameLabel(translate.t("username"));
        setPasswordTranslate(translate.t("password"));

        return usernameOk && passwordOk;
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        setGeneralError(null);

        Api.post(`${URLS.AUTH}local_fallback/`, { username, password })
            .then((response) => {
                const access = response.data?.access;
                if (!access) {
                    setGeneralError(translate.t("invalid_credentials"));
                    return;
                }
                authTokenStorage.set(access);
                publishAuthMessage({ type: "token", access });
                const pendingPath = consumePendingRedirect();
                window.location.assign(pendingPath || "/");
            })
            .catch(() => {
                setGeneralError(translate.t("invalid_credentials"));
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    if (checkingStatus) {
        return <Loading open={true} />;
    }

    if (!fallbackAvailable) {
        return null;
    }

    return (
        <div
            className="bg-login"
            style={{
                backgroundImage: `url(${bgLogin})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center center",
                position: "absolute",
                bottom: 0,
                top: 0,
                right: 0,
                left: 0,
            }}>
            <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: "100%" }}>
                <Grid item sx={{ width: "400px" }}>
                    <Card elevation={4}>
                        <CardContent>
                            <Box
                                marginTop={4}
                                marginBottom={5}
                                component={"img"}
                                sx={{
                                    height: 32,
                                }}
                                alt="Logo P-Mat"
                                src={logo}
                            />
                            <Grid container alignItems="center" justifyContent="center">
                                <Grid item sx={{ width: "90%" }}>
                                    <Box component="form" onSubmit={handleSubmit}>
                                        <Grid container spacing={2} direction="column">
                                            {generalError && (
                                                <Grid item>
                                                    <Alert severity="error">{generalError}</Alert>
                                                </Grid>
                                            )}
                                            <Grid item>
                                                <TextField
                                                    error={Boolean(usernameMessage)}
                                                    name="username"
                                                    label={usernameLabel}
                                                    helperText={usernameMessage}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="inp_username"
                                                    value={username}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                        setUsername(event.target.value)
                                                    }
                                                    autoFocus={true}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <TextField
                                                    error={Boolean(passwordMessage)}
                                                    name="password"
                                                    type="password"
                                                    label={passwordTranslate}
                                                    helperText={passwordMessage}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="inp_password"
                                                    value={password}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                        setPassword(event.target.value)
                                                    }
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    style={{
                                                        width: "100%",
                                                        height: "52px",
                                                        boxShadow: "none",
                                                    }}
                                                    className="bt-login"
                                                    size="large"
                                                    color="primary"
                                                    variant="contained"
                                                    type="submit"
                                                    id="btn_submit"
                                                    disabled={submitting || !username || !password}>
                                                    {translate.t("enter")}
                                                </Button>
                                            </Grid>
                                            <Grid marginTop={2} container justifyContent="center">
                                                <Grid item>
                                                    <ButtonGroup
                                                        variant="outlined"
                                                        size="small"
                                                        style={{ alignItems: "center" }}>
                                                        <Button
                                                            id="btn_pt"
                                                            onClick={() => handleLanguageChange("pt")}>
                                                            PT
                                                        </Button>
                                                        <Button
                                                            id="btn_en"
                                                            onClick={() => handleLanguageChange("en")}>
                                                            EN
                                                        </Button>
                                                    </ButtonGroup>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
};

export default LoginComponent;
